import { useState } from 'react'
import { PageContainer } from '@/components/layout/PageContainer'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  LayoutGrid,
  Plus,
  MoreVertical,
  Star,
  StarOff,
  Trash2,
  AlertCircle,
  RefreshCw,
  Loader2,
  Settings,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { useLineMessagingConfig } from '@/features/storefront/settings/useLineMessagingConfig'
import {
  useLineOARichMenus,
  useDeleteRichMenu,
  useSetDefaultRichMenu,
  useClearDefaultRichMenu,
} from './useLineOARichMenus'
import type { LineRichMenu } from '@/lib/api/controlPlaneClient'

// --- Rich Menu Card ---

function RichMenuCard({
  menu,
  isDefault,
  onSetDefault,
  onClearDefault,
  onDelete,
}: {
  menu: LineRichMenu
  isDefault: boolean
  onSetDefault: () => void
  onClearDefault: () => void
  onDelete: () => void
}) {
  return (
    <Card className={`rounded border-0 bg-white ${isDefault ? 'ring-2 ring-green-500/30' : ''}`}>
      <CardContent className="p-4 flex items-center gap-4">
        <div className="w-16 h-12 rounded-sm bg-gray-100 flex items-center justify-center flex-shrink-0">
          <LayoutGrid className="w-6 h-6 text-gray-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold truncate">{menu.name}</p>
            {isDefault && (
              <Badge variant="secondary" className="bg-green-50 text-green-700 text-[10px]">
                Default
              </Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            {menu.areas?.length || 0} area{(menu.areas?.length || 0) !== 1 ? 's' : ''}
            {' · '}
            {menu.size?.width}×{menu.size?.height}
          </p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              <MoreVertical className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {isDefault ? (
              <DropdownMenuItem onClick={onClearDefault}>
                <StarOff className="w-4 h-4 mr-2" />
                Clear default
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem onClick={onSetDefault}>
                <Star className="w-4 h-4 mr-2" />
                Set as default
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onClick={onDelete}
              className="text-red-600 focus:text-red-600"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </CardContent>
    </Card>
  )
}

// --- Main Page ---

export function LineOARichMenusPage() {
  const { status, isLoading: isLoadingStatus } = useLineMessagingConfig()
  const {
    data,
    isLoading: isLoadingMenus,
    isError,
    refetch,
  } = useLineOARichMenus({ enabled: !!status?.connected })

  const deleteMenu = useDeleteRichMenu()
  const setDefault = useSetDefaultRichMenu()
  const clearDefault = useClearDefaultRichMenu()

  const [deleteTarget, setDeleteTarget] = useState<string | null>(null)
  const [deleteName, setDeleteName] = useState('')

  // Loading connection status
  if (isLoadingStatus) {
    return (
      <PageContainer>
        <div className="p-6 flex items-center justify-center min-h-[200px]">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      </PageContainer>
    )
  }

  // Not connected
  if (!status?.connected) {
    return (
      <PageContainer>
        <div className="p-6 space-y-4">
          <h2 className="text-2xl font-semibold">Rich Menus</h2>
          <Card className="rounded-2xl border-0 max-w-lg">
            <CardContent className="p-6 space-y-4 text-center">
              <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto">
                <Settings className="w-6 h-6 text-gray-400" />
              </div>
              <div>
                <p className="font-medium">Connect LINE Messaging API</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Connect your LINE Messaging API token in Storefront Settings to
                  manage rich menus.
                </p>
              </div>
              <Button asChild>
                <Link to="/storefront/settings">Go to Settings</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </PageContainer>
    )
  }

  const menus = data?.menus || []
  const defaultId = data?.default_rich_menu_id || null
  const defaultMenu = menus.find((m) => m.richMenuId === defaultId)
  const otherMenus = menus.filter((m) => m.richMenuId !== defaultId)

  const handleSetDefault = (richMenuId: string) => {
    setDefault.mutate(richMenuId, {
      onSuccess: () => toast.success('Default rich menu updated.'),
      onError: (err) => toast.error(err.message || 'Failed to set default.'),
    })
  }

  const handleClearDefault = () => {
    clearDefault.mutate(undefined, {
      onSuccess: () => toast.success('Default rich menu cleared.'),
      onError: (err) => toast.error(err.message || 'Failed to clear default.'),
    })
  }

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return
    deleteMenu.mutate(deleteTarget, {
      onSuccess: () => {
        toast.success('Rich menu deleted.')
        setDeleteTarget(null)
      },
      onError: (err) => toast.error(err.message || 'Failed to delete.'),
    })
  }

  return (
    <PageContainer>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-semibold">Rich Menus</h2>
          <Button asChild size="sm">
            <Link to="/line-oa/rich-menus/new">
              <Plus className="w-4 h-4 mr-1.5" />
              New Rich Menu
            </Link>
          </Button>
        </div>

        {/* Error banner */}
        {isError && (
          <div className="flex items-center gap-3 p-4 rounded bg-red-50 text-red-700">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <p className="text-sm flex-1">
              Couldn&apos;t load rich menus from LINE. Try again.
            </p>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => refetch()}
              className="text-red-700 hover:text-red-800 hover:bg-red-100"
            >
              <RefreshCw className="w-4 h-4 mr-1.5" />
              Retry
            </Button>
          </div>
        )}

        {/* Loading */}
        {isLoadingMenus && (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="rounded border-0 bg-white">
                <CardContent className="p-4 flex items-center gap-4">
                  <Skeleton className="w-16 h-12 rounded-sm" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-20" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Empty state */}
        {!isLoadingMenus && !isError && menus.length === 0 && (
          <Card className="rounded-2xl border-0 max-w-lg">
            <CardContent className="p-6 space-y-4 text-center">
              <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto">
                <LayoutGrid className="w-6 h-6 text-gray-400" />
              </div>
              <div>
                <p className="font-medium">No rich menus yet</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Rich menus appear at the bottom of your LINE chat — they&apos;re
                  the first thing customers see.
                </p>
              </div>
              <Button asChild>
                <Link to="/line-oa/rich-menus/new">
                  <Plus className="w-4 h-4 mr-1.5" />
                  Create your first rich menu
                </Link>
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Menu list */}
        {!isLoadingMenus && !isError && menus.length > 0 && (
          <div className="space-y-6">
            {/* Default menu section */}
            {defaultMenu && (
              <div>
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                  Default Menu
                </h3>
                <RichMenuCard
                  menu={defaultMenu}
                  isDefault
                  onSetDefault={() => {}}
                  onClearDefault={handleClearDefault}
                  onDelete={() => {
                    setDeleteTarget(defaultMenu.richMenuId)
                    setDeleteName(defaultMenu.name)
                  }}
                />
              </div>
            )}

            {/* Other menus */}
            {otherMenus.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                  {defaultMenu ? 'Other Menus' : 'All Menus'}
                </h3>
                <div className="space-y-2">
                  {otherMenus.map((menu) => (
                    <RichMenuCard
                      key={menu.richMenuId}
                      menu={menu}
                      isDefault={false}
                      onSetDefault={() => handleSetDefault(menu.richMenuId)}
                      onClearDefault={() => {}}
                      onDelete={() => {
                        setDeleteTarget(menu.richMenuId)
                        setDeleteName(menu.name)
                      }}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete rich menu?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete &quot;{deleteName}&quot; from LINE.
              This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMenu.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={deleteMenu.isPending}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deleteMenu.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
              ) : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  )
}
